import moment from 'moment';
import { NOTIFICATION_STATUS_CODE } from '../../enums/notification-status-code.enum';
import { isDefined } from '../../utilities/is-defined';

/**
 * Pure rules shared by the legacy `TravelStatusUpdateService` and the NgRx-backed
 * `TravelStatusUpdateEffects`, so the two implementations cannot drift on the decisions that
 * matter: which statuses count as "on the road or on site", whether a step was skipped, and the
 * early-travel violation check.
 */

/** True when the next status puts the technician on the road or on site: TRAV, OSR, ROSR or FCC. */
export function isTravelOrOnSiteStatus(nextStatus: NOTIFICATION_STATUS_CODE): boolean {
    return (
        nextStatus === NOTIFICATION_STATUS_CODE.TRAV ||
        nextStatus === NOTIFICATION_STATUS_CODE.OSR ||
        nextStatus === NOTIFICATION_STATUS_CODE.ROSR ||
        nextStatus === NOTIFICATION_STATUS_CODE.FCC
    );
}

/**
 * True when the technician jumped a step in DISP -> ETA -> TRAV -> OSR, so the skipped statuses
 * have to be written before the requested one.
 */
export function isStatusSkipped(
    currentStatus: NOTIFICATION_STATUS_CODE,
    nextStatus: NOTIFICATION_STATUS_CODE,
): boolean {
    return (
        (currentStatus === NOTIFICATION_STATUS_CODE.DISP && nextStatus !== NOTIFICATION_STATUS_CODE.ETA) ||
        ((currentStatus === NOTIFICATION_STATUS_CODE.ETA || currentStatus === NOTIFICATION_STATUS_CODE.DURA) &&
            nextStatus !== NOTIFICATION_STATUS_CODE.TRAV &&
            nextStatus !== NOTIFICATION_STATUS_CODE.ROSR &&
            nextStatus !== NOTIFICATION_STATUS_CODE.FCC)
    );
}

/**
 * True when the update is attempted earlier than the configured limit before the planned start.
 * Throws on an undefined or invalid moment, as the legacy check did.
 */
export function isEarlyTravelRuleViolated(
    plannedStart: moment.Moment,
    actualDate: moment.Moment,
    earlyTravelHoursAllowed: number,
): boolean {
    if (!isDefined(earlyTravelHoursAllowed) || earlyTravelHoursAllowed <= 0) {
        // no limit passed or zero/negative -> no violation of rule
        return false;
    }

    if (!isDefined(plannedStart) || !plannedStart.isValid()) {
        throw new Error('TravelStatusUpdateComponent::calculateEarlyTravelViolation: Invalid plannedStart');
    }

    if (!isDefined(actualDate) || !actualDate.isValid()) {
        throw new Error('TravelStatusUpdateComponent::calculateEarlyTravelViolation: Invalid actualDate');
    }

    return plannedStart.diff(actualDate, 'hours', true) > earlyTravelHoursAllowed;
}
